<template>
  <!-- Responsive metrics grid -->
  <div class="metrics-row p-4 gap-4">
      <div
        v-for="m in metrics"
        :key="m.key"
        class="metric-card dx-card responsive-paddings rounded-xl"
      >
        <!-- icon top-right -->
        <DxButton
          class="metric-icon"
          :icon="m.icon"
          styling-mode="text"
          :style="{ color: m.color }"
        />

        <!-- label + number -->
        <div>
          <div class="text-sm uppercase tracking-wide text-gray-300" :style="{ marginBottom: '.5rem' }">{{ m.label }}</div>
          <div :style="{ color: m.color, fontSize: '2rem', lineHeight: '1.15', fontWeight: '700' }">
            {{ m.value }}
          </div>
        </div>
      </div>
  </div>
</template>

<script setup>
import DxButton from 'devextreme-vue/button';

import { computed, watch } from 'vue';
import useTankStats from './temp-data-and-supports/useTankStats.js';
import { useForecast } from './temp-data-and-supports/useForecastApi.js';
import filtersState from './temp-data-and-supports/useForecastFilters.js';
import { selectedRange, selectedMode } from './temp-data-and-supports/useForecastViewState.js';

const { totalTanks, criticalTanks, totalVolumeL } = useTankStats();

// optional – pull forecast error metrics if already loaded
const { data, load } = useForecast();

// trigger forecast fetch when operator/chemical change
// trigger forecast fetch when operator/chemical/scope change
watch(
  () => [filtersState.operatorId, filtersState.chemical, filtersState.selectedScope],
  ([op, chem, scope]) => {
    if (op && chem) {
      const scopeType = scope.type === 'operator' ? null : scope.type;
      const scopeId = scope.id;
      load(op, chem, 365, scopeType, scopeId);
    }
  },
  { immediate: true }
);

const metrics = computed(() => {
  // Respect the mode & horizon chosen in the chart so the summary values match
  const metricKey   = selectedMode.value === 'level' ? 'level' : 'consumption';
  const fullFcArr   = data.value?.[metricKey]?.forecast ?? [];

  // Limit to the number of days currently displayed on the chart
  const horizon     = selectedRange.value;
  const forecastArr = fullFcArr.slice(0, horizon);

  let lower = '-';
  let upper = '-';

  if (forecastArr.length) {
    const lowers = forecastArr.map(p => Number(p.yhat_lower)).filter(v => !Number.isNaN(v));
    const uppers = forecastArr.map(p => Number(p.yhat_upper)).filter(v => !Number.isNaN(v));

    if (lowers.length) lower = Math.min(...lowers).toFixed(1);
    if (uppers.length) upper = Math.max(...uppers).toFixed(1);
  }

  return [
    { key: 'critical', label: 'Critical Tanks',  value: criticalTanks.value, color: '#f15e50', icon: 'warning' },
    { key: 'lower',    label: 'Forecast Lower', value: lower,               color: '#ffd61f', icon: 'arrowdown' },
    { key: 'upper',    label: 'Forecast Upper', value: upper,               color: '#f4711f', icon: 'arrowup' },
    { key: 'total',        label: 'Total Tanks',    value: totalTanks.value,    color: '#8ec959', icon: 'box' },
    { key: 'totalVolume',  label: 'Total Volume',  value: formatVolume(totalVolumeL.value), color: '#1f78b4', icon: 'water' },
  ];
});

function formatVolume(val) {
  if (!val || Number.isNaN(val)) return '-';
  // format with thousand separators and append L
  return `${Math.round(val).toLocaleString()} L`;
}
</script>

<style scoped>
.metrics-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 1rem; /* 16px space between cards */
}

.metric-card {
  backdrop-filter: blur(6px);
  min-width: 0;           /* allow shrinking */
  position: relative;
  background-color: #2b2b34;
}

.metric-icon {
  position: absolute;
  top: 0.5rem;  /* 8px */
  right: 0.5rem;
  padding: 0.25rem; /* slight extra tappable area */
}
</style>
